import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What is the main benefit of meal planning according to the module?",
    options: [
      "It saves time and money while improving nutrition",
      "It eliminates the need to cook",
      "It allows you to eat out every day",
      "It makes cooking more complicated"
    ],
    correctAnswer: 0,
    explanation: "Meal planning helps you eat healthier, save money, reduce waste, and manage your time better. By planning ahead, you can make more nutritious choices and avoid impulsive unhealthy purchases."
  },
  {
    question: "Which of these is recommended as a healthy beverage choice with meals?",
    options: [
      "Soda",
      "Fruit juice with added sugar",
      "Water or milk",
      "Energy drinks"
    ],
    correctAnswer: 2,
    explanation: "Water or milk are ideal beverages to accompany meals. They support hydration and nutrition without adding unnecessary sugars or artificial ingredients that can interfere with digestion."
  },
  {
    question: "What should you do before creating a shopping list?",
    options: [
      "Go to the store immediately",
      "Check what you already have in your kitchen",
      "Buy whatever looks good",
      "Ignore your pantry contents"
    ],
    correctAnswer: 1,
    explanation: "Checking your inventory first prevents buying duplicates and helps you use what you already have. This reduces waste and saves money while ensuring you only buy what you actually need."
  },
  {
    question: "Why is it important not to shop when you're hungry?",
    options: [
      "You'll buy less food",
      "You'll make impulsive purchases of unhealthy items",
      "The store will be too crowded",
      "You'll forget what you need"
    ],
    correctAnswer: 1,
    explanation: "Shopping while hungry often leads to buying comfort foods, snacks, and impulse items that aren't on your list. Eating something before shopping helps you stick to your planned purchases."
  },
  {
    question: "Which meal planning strategy can reduce food expenses by 25-30%?",
    options: [
      "Eating out every day",
      "Planning meals and shopping with a list",
      "Buying expensive imported foods",
      "Throwing away unused ingredients"
    ],
    correctAnswer: 1,
    explanation: "Meal planning with a shopping list can reduce food expenses by 25-30% by preventing impulse buys, reducing waste from unused ingredients, and allowing you to take advantage of sales and bulk buying opportunities."
  }
];

const NutritionalModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('nutritionalM4');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 4 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Practical meal planning strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Smart shopping techniques</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Making healthy food choices in daily life</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Try planning your meals for the next few days!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Nutritional Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Hydration and Digestive Health</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about hydration and digestive health.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule4QuizPage;

